import { useMemo, useState, useRef, useCallback, useEffect } from "react";

// Components
import Modal from "components/organism/modal-v2";
import Button from "components/v1/Button";

// Assets
import { ReactComponent as XIcon } from "assets/cross.svg";
import IArrowShortail from "assets/i-arrow-shortail";

// Apis
import { useGetMarketingSofiaInfinite } from "api/sofia-magic-studio/query";

const RadioButton = ({
  id,
  full_name,
  credit_amount,
  selectedId,
  onSelect,
}) => {
  const isChecked = selectedId === id;
  return (
    <div className="flex items-center gap-2 jusitfy-between border-b border-dashed border-[#D8D8D9] py-2 w-full">
      <div className="w-[calc(100%-16px-8px)]">
        <p className="text-[13px] font-inter">{full_name}</p>
        <p className="text-[11px] font-inter text-[#808285]">
          Kredit: <span className="text-[#252526]">{credit_amount}</span>
        </p>
      </div>
      <input
        className="w-4 h-4"
        type="radio"
        checked={isChecked}
        onChange={() => onSelect(id)}
      />
    </div>
  );
};

const ModalShareCredit = ({ onSubmit, onClose, isOpen, balance, ...props }) => {
  const [step, setStep] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [creditAmount, setCreditAmount] = useState(null);
  const observerTarget = useRef(null);

  const {
    data: marketingData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetMarketingSofiaInfinite({ enabled: isOpen });

  const marketingOptions = useMemo(() => {
    if (!marketingData?.pages) return [];
    return marketingData.pages.flatMap(page => 
      page?.data?.data?.map((item) => ({
        credit_amount: item.credit_amount,
        id: item.id,
        full_name: item.full_name,
      })) || []
    );
  }, [marketingData]);

  const handleObserver = useCallback((entries) => {
    const [target] = entries;
    if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const option = { threshold: 0, rootMargin: '50px' };
    const observer = new IntersectionObserver(handleObserver, option);
    
    observer.observe(element);
    
    return () => {
      observer.unobserve(element);
    };
  }, [handleObserver]);

  const RenderContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div className="px-6 py-2 h-[353px] overflow-y-auto">
              <p className="text-[13px] font-inter italic text-[#808285] py-2 border-b border-dashed border-[#D8D8D9]">
                Pilih marketing yang akan menerima kredit.
              </p>
              {marketingOptions?.map((item) => (
                <RadioButton
                  key={item.id}
                  id={item.id}
                  full_name={item.full_name}
                  credit_amount={item.credit_amount}
                  selectedId={selectedId}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}
              <div ref={observerTarget} className="h-4" />
              {isFetchingNextPage && (
                <div className="text-center py-2">
                  <p className="text-[11px] text-[#808285]">Memuat lebih banyak...</p>
                </div>
              )}
            </div>
            <div className="px-4 py-2 border-b border-solid border-[#D8D8D9] flex items-center justify-between">
              <p className="text-[11px] font-inter italic text-[#808285]">
                <span className="text-[#F04438]">*</span>Kredit yang telah
                dibagikan tidak dapat ditarik kembali.
              </p>
              <Button
                variant="primary"
                onClick={() => setStep(2)}
                text="Selanjutnya"
                icon={IArrowShortail}
                iconPosition="right"
                iconColor="#fff"
                rotateIcon="180"
              />
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="px-6 py-2 h-[353px] overflow-y-auto">
              <p className="text-[13px] font-inter italic text-[#808285] py-2 border-b border-dashed border-[#D8D8D9]">
                Berapa jumlah kredit yang akan dibagikan?
              </p>
              <p className="text-[13px] font-inter text-[#808285] py-2">
                Jumlah kredit saat ini:{" "}
                <span className="text-[#252526]">
                  {balance?.toLocaleString("id-ID") || "0"}
                </span>
              </p>
              <div className="flex items-center gap-2">
                <p className="text-[13px] font-inter text-[#808285] py-2 whitespace-nowrap">
                  Kredit dibagikan <span className="text-[#F04438]">*</span>
                </p>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Masukkan jumlah kredit"
                  className="w-full p-2 border border-[#D8D8D9] rounded-[4px] border border-solid border-[#D8D8D9]"
                />
              </div>
            </div>
            <div className="px-4 py-2 border-b border-solid border-[#D8D8D9] flex items-center justify-between gap-2">
              <p className="text-[11px] font-inter italic text-[#808285]">
                <span className="text-[#F04438]">*</span>Kredit yang telah
                dibagikan tidak dapat ditarik kembali.
              </p>
              <Button
                type="secondary"
                onClick={() => setStep(1)}
                text="Kembali"
                icon={IArrowShortail}
                iconPosition="left"
              />
              <Button
                onClick={() => setStep(2)}
                text="Selanjutnya"
                icon={IArrowShortail}
                iconPosition="right"
                iconColor="#fff"
                rotateIcon="180"
              />
            </div>
          </>
        );
      case 3:
        return (
          <>
            <div className="px-6 py-2 h-[353px] overflow-y-auto">
              <p className="text-[13px] font-inter italic text-[#808285] py-2 border-b border-dashed border-[#D8D8D9]">
                Bagikan kredit?
              </p>
              <p className="text-[13px] font-inter text-[#808285] py-2">
                Setelah dibagikan, sisa kredit Anda menjadi {balance - creditAmount}.
              </p>
            </div>
            <div className="px-4 py-2 border-b border-solid border-[#D8D8D9] flex items-center justify-between gap-2">
              <Button
                type="secondary"
                onClick={() => setStep(2)}
                text="Kembali"
                icon={IArrowShortail}
                iconPosition="left"
              />
              <Button
                onClick={() => onSubmit({ credits: creditAmount, user_id: selectedId })}
                text="Bagikan"
                iconPosition="right"
                iconColor="#fff"
                rotateIcon="180"
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...props}>
      <div
        className="w-[431px] z-[42] bg-white rounded-md border-[0.5px] border-solid border-[#ECECEC] 
        shadow-[0px_1px_3px_0px_rgba(16,16,16,0.10),_0px_1px_2px_0px_rgba(16,16,16,0.06)]"
      >
        <div className="flex justify-between px-4 py-2 border-b border-solid border-[#D8D8D9]">
          <p className="text-[17px] font-inter-semibold">Bagikan kredit</p>
          <XIcon onClick={onClose} />
        </div>

        {RenderContent()}
      </div>
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ModalShareCredit isOpen={isOpen} {...props} />;
};

export default Wrapper;
